import { useEffect, useState } from 'react'
import {
  Loader2, Plus, Trash2, Check, Copy, Building2, Globe, KeyRound,
  Database, Users, ChevronLeft, AlertTriangle, Power,
} from 'lucide-react'
import api from '../../services/api'

/**
 * Super Admin — the companies that run ProTrack under their own name.
 *
 * This is the landlord's screen, and only a landlord's: it creates a company, proves the domain
 * it may speak for, holds the details of its sign-in and makes its database. It does not open
 * any of them. What happens inside a company is its own administrator's screen, not this one.
 *
 * Laid out as the real sequence rather than a list of fields, because the steps genuinely block
 * one another — a domain cannot be verified before it is added, sign-in cannot be turned on
 * before a domain is proved, and people cannot be added before there is a database to put them
 * in. The checklist is that dependency, shown.
 */
export default function Organisations() {
  const [orgs, setOrgs] = useState(null)
  const [error, setError] = useState(null)
  const [openId, setOpenId] = useState(null)

  const load = async () => {
    setError(null)
    try {
      const { data } = await api.get('/organisations/')
      setOrgs(data?.items || [])
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the organisations.')
      setOrgs([])
    }
  }
  useEffect(() => { load() }, [])

  if (orgs === null) {
    return <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
  }

  const open = orgs.find(o => o.id === openId)
  if (open) {
    return <OrgDetail org={open} reload={load} back={() => setOpenId(null)} />
  }

  return (
    <div className="space-y-5">
      {error && <Problem>{error}</Problem>}

      <NewOrg reload={load} setError={setError} />

      {orgs.length === 0 ? (
        <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg px-4 py-6 text-center">
          No organisations yet. The first one can be created above — a record only; nothing is
          made until you provision its database.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {orgs.map(o => (
            <li key={o.id}>
              <button
                onClick={() => setOpenId(o.id)}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-center gap-3"
              >
                <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900 truncate">
                    {o.name}
                    {!o.is_active && <span className="ml-2 text-xs font-normal text-gray-500">(switched off)</span>}
                  </span>
                  <span className="block text-xs text-gray-500 truncate">
                    {o.slug} · {o.member_count} {o.member_count === 1 ? 'person' : 'people'}
                  </span>
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <Chip on={o.domains?.some(d => d.verified)} label="Domain" />
                  <Chip on={o.sso_enabled} label="Sign-in" />
                  <Chip on={o.database_ready} label="Database" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/* ── the pieces used everywhere ───────────────────────────────────────────── */

function Problem({ children }) {
  return (
    <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{children}</span>
    </p>
  )
}

function Done({ children }) {
  return (
    <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
      {children}
    </p>
  )
}

/** State in shape as well as words — the row can be read without reading it. */
function Chip({ on, label }) {
  return (
    <span className={`text-[11px] px-1.5 py-0.5 rounded border ${on
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-gray-50 text-gray-400 border-gray-200'}`}>
      {label}
    </span>
  )
}

function Field({ label, help, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <input
        {...rest}
        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      />
      {help && <span className="block text-xs text-gray-500 mt-1">{help}</span>}
    </label>
  )
}

function Button({ children, kind = 'primary', busy, ...rest }) {
  const styles = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-indigo-300',
    plain: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:text-gray-400',
    danger: 'bg-white text-red-700 border border-red-200 hover:bg-red-50 disabled:text-red-300',
  }[kind]
  return (
    <button
      {...rest}
      disabled={busy || rest.disabled}
      className={`text-sm font-medium rounded-lg px-3 py-2 transition inline-flex items-center gap-1.5 ${styles}`}
    >
      {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {children}
    </button>
  )
}

/** A DNS record is copied, never retyped — one wrong character and verification just fails. */
function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-16 shrink-0">{label}</span>
      <code className="text-xs bg-white border border-gray-200 rounded px-2 py-1 flex-1 min-w-0 overflow-x-auto whitespace-nowrap">
        {value}
      </code>
      <button
        onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
        className="text-gray-400 hover:text-gray-700 shrink-0"
        title="Copy"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  )
}

/* ── creating one ─────────────────────────────────────────────────────────── */

function NewOrg({ reload, setError }) {
  const [show, setShow] = useState(false)
  const [form, setForm] = useState({ name: '', slug: '', contact_email: '' })
  const [busy, setBusy] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      // Blank slug is left out entirely rather than sent empty — the server derives one from
      // the name, and an empty string is not the same request as an absent field.
      const body = { name: form.name }
      if (form.slug.trim()) body.slug = form.slug.trim()
      if (form.contact_email.trim()) body.contact_email = form.contact_email.trim()
      await api.post('/organisations/', body)
      setForm({ name: '', slug: '', contact_email: '' })
      setShow(false)
      await reload()
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not create the organisation.')
    } finally {
      setBusy(false)
    }
  }

  if (!show) {
    return <Button kind="plain" onClick={() => setShow(true)}><Plus className="w-4 h-4" /> New organisation</Button>
  }

  return (
    <form onSubmit={submit} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Company name" required value={form.name}
               onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Acme Consulting" />
        <Field label="Short name" value={form.slug}
               onChange={e => setForm({ ...form, slug: e.target.value })}
               placeholder="acme" help="Their web address. Left blank, it comes from the name." />
        <Field label="Who to contact" type="email" value={form.contact_email}
               onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="ops@acme.com" />
      </div>
      <div className="flex gap-2">
        <Button type="submit" busy={busy}>Create</Button>
        <Button type="button" kind="plain" onClick={() => setShow(false)}>Cancel</Button>
      </div>
    </form>
  )
}

/* ── one company, set up in the order the steps actually depend on ────────── */

function OrgDetail({ org, reload, back }) {
  const [error, setError] = useState(null)
  const [note, setNote] = useState(null)

  const domainProved = org.domains?.some(d => d.verified)
  const configFilled = Boolean(org.sso_issuer && org.sso_client_id && org.sso_secret_set)

  // Numbered because it is a real sequence: each step is refused until the one above it is done.
  const steps = [
    { label: 'Organisation created', done: true },
    { label: 'Domain verified', done: domainProved },
    { label: 'Switched on', done: Boolean(org.is_active) },
    { label: 'Sign-in details filled in', done: configFilled },
    { label: 'Sign-in turned on', done: org.sso_enabled },
    { label: 'Proved by a real sign-in', done: Boolean(org.sso_verified_at) },
    { label: 'Database of their own', done: org.database_ready },
  ]

  const after = async (message) => { setNote(message || null); await reload() }

  const [busy, setBusy] = useState(null)
  const switchOn = async (on) => {
    setBusy('activate'); setError(null)
    try {
      await api.post(`/organisations/${org.id}/activate?active=${on ? 'true' : 'false'}`)
      await after(on
        ? `${org.name} is on. You can now add their people.`
        : `${org.name} is off. Nothing is served at their address.`)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not change that.')
    } finally { setBusy(null) }
  }

  return (
    <div className="space-y-5">
      <button onClick={back} className="text-sm text-gray-500 hover:text-gray-800 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> All organisations
      </button>

      <div>
        <h2 className="text-lg font-semibold text-gray-900">{org.name}</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          {org.slug}
          {org.contact_email && <> · {org.contact_email}</>}
          {!org.is_active && <> · <span className="text-red-600">switched off</span></>}
        </p>
      </div>

      {error && <Problem>{error}</Problem>}
      {note && <Done>{note}</Done>}

      <ol className="border border-gray-200 rounded-lg divide-y divide-gray-100 text-sm">
        {steps.map((s, i) => (
          <li key={s.label} className="px-4 py-2 flex items-center gap-3">
            <span className={`w-5 h-5 rounded-full text-[11px] font-medium flex items-center justify-center shrink-0 ${s.done
              ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
              {s.done ? <Check className="w-3 h-3" /> : i + 1}
            </span>
            <span className={s.done ? 'text-gray-900' : 'text-gray-500'}>{s.label}</span>
          </li>
        ))}
      </ol>

      {/* A COMPANY IS CREATED SWITCHED OFF, AND SOMETHING HAS TO SWITCH IT ON.
          Off is the right starting state — a half-set-up company should not be servable — but
          the checklist above showed six steps and none of them was this one, so setup stalled
          here with nothing to click. Refused until a domain is proved, and the reason is on
          the button rather than in the error it would otherwise return. */}
      {!org.is_active && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-2">
          <p className="text-sm text-amber-900">
            <strong>{org.name} is switched off.</strong> Nothing is served at their address and
            nobody can be added to it while it is off.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Button busy={busy === 'activate'} disabled={!domainProved}
                    onClick={() => switchOn(true)}>
              <Power className="w-3.5 h-3.5" /> Switch {org.name} on
            </Button>
            <span className="text-xs text-amber-800">
              {domainProved
                ? 'Their domain is proved, so this is ready.'
                : 'Waiting on a verified domain — do that first, below.'}
            </span>
          </div>
        </div>
      )}

      {org.is_active && (
        <div className="flex justify-end">
          <Button kind="plain" busy={busy === 'activate'} onClick={() => switchOn(false)}>
            <Power className="w-3.5 h-3.5" /> Switch off
          </Button>
        </div>
      )}

      <Domains org={org} after={after} setError={setError} />
      <SignIn org={org} after={after} setError={setError}
              ready={domainProved && configFilled} domainProved={domainProved} />
      <TheirDatabase org={org} after={after} setError={setError} />
      <People org={org} after={after} setError={setError} />
    </div>
  )
}

function Panel({ icon: Icon, title, blurb, children }) {
  return (
    <section className="border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {blurb && <p className="text-xs text-gray-500 mt-0.5">{blurb}</p>}
        </div>
      </div>
      {children}
    </section>
  )
}

/* ── step 2: the domain they may speak for ────────────────────────────────── */

function Domains({ org, after, setError }) {
  const [domain, setDomain] = useState('')
  const [busy, setBusy] = useState(null)

  const add = async (e) => {
    e.preventDefault()
    setBusy('add'); setError(null)
    try {
      await api.post(`/organisations/${org.id}/domains`, { domain: domain.trim() })
      setDomain('')
      await after(null)
    } catch (e) { setError(e.response?.data?.detail || 'Could not add that domain.') }
    finally { setBusy(null) }
  }

  const verify = async (d) => {
    setBusy(d.id); setError(null)
    try {
      const { data } = await api.post(`/organisations/${org.id}/domains/${d.id}/verify`)
      await after(data?.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not verify that domain yet.') }
    finally { setBusy(null) }
  }

  const remove = async (d) => {
    setBusy(d.id); setError(null)
    try {
      await api.delete(`/organisations/${org.id}/domains/${d.id}`)
      await after(null)
    } catch (e) { setError(e.response?.data?.detail || 'Could not remove that domain.') }
    finally { setBusy(null) }
  }

  return (
    <Panel icon={Globe} title="Their domain"
           blurb="Proof of which email addresses this company may sign in. Everything else waits on it.">
      <ul className="space-y-2">
        {(org.domains || []).map(d => (
          <li key={d.id} className="border border-gray-200 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-900 flex-1 truncate">{d.domain}</span>
              {d.verified
                ? <span className="text-xs text-green-700 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> verified</span>
                : <Button kind="plain" busy={busy === d.id} onClick={() => verify(d)}>Check now</Button>}
              <button onClick={() => remove(d)} disabled={busy === d.id}
                      className="text-gray-300 hover:text-red-600 disabled:opacity-40" title="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {!d.verified && (
              <div className="mt-2 pt-2 border-t border-gray-100 space-y-1.5 bg-gray-50 -mx-3 -mb-2 px-3 py-2 rounded-b-lg">
                <p className="text-xs text-gray-600">
                  Add this TXT record to their DNS, then check. It can take a few minutes to
                  appear — a failed check usually means "not yet", not "wrong".
                </p>
                <CopyRow label="Name" value={d.record_name} />
                <CopyRow label="Value" value={d.record_value} />
              </div>
            )}
          </li>
        ))}
      </ul>

      <form onSubmit={add} className="flex gap-2 items-end">
        <div className="flex-1">
          <Field label="Add a domain" required value={domain}
                 onChange={e => setDomain(e.target.value)} placeholder="acme.com" />
        </div>
        <Button type="submit" busy={busy === 'add'}>Add</Button>
      </form>
    </Panel>
  )
}

/* ── step 3 and 4: their sign-in ──────────────────────────────────────────── */

function SignIn({ org, after, setError, ready, domainProved }) {
  const [form, setForm] = useState({
    sso_provider: org.sso_provider || 'oidc',
    sso_issuer: org.sso_issuer || '',
    sso_client_id: org.sso_client_id || '',
    sso_client_secret: '',
  })
  const [busy, setBusy] = useState(null)

  const save = async (e) => {
    e.preventDefault()
    setBusy('save'); setError(null)
    try {
      // The secret is only sent when something was typed. Sending an empty one would either
      // overwrite the stored secret with nothing or, worse, read as a change and switch
      // sign-in off for a company that is working.
      const body = {
        sso_provider: form.sso_provider,
        sso_issuer: form.sso_issuer.trim(),
        sso_client_id: form.sso_client_id.trim(),
      }
      if (form.sso_client_secret.trim()) body.sso_client_secret = form.sso_client_secret.trim()
      await api.put(`/organisations/${org.id}`, body)
      setForm({ ...form, sso_client_secret: '' })
      await after('Saved. Saving these details always switches sign-in off, because a setting nobody has tested is not one to leave on.')
    } catch (e) { setError(e.response?.data?.detail || 'Could not save the sign-in details.') }
    finally { setBusy(null) }
  }

  const toggle = async (on) => {
    setBusy('toggle'); setError(null)
    try {
      const { data } = await api.post(`/organisations/${org.id}/sso/${on ? 'enable' : 'disable'}`)
      await after(data?.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not change that.') }
    finally { setBusy(null) }
  }

  return (
    <Panel icon={KeyRound} title="Their sign-in"
           blurb="They hold the passwords; we never see one. The secret is stored encrypted and cannot be read back.">
      <form onSubmit={save} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Method</span>
            <select
              value={form.sso_provider}
              onChange={e => setForm({ ...form, sso_provider: e.target.value })}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="oidc">OIDC — Google, Microsoft, Okta</option>
              <option value="none">None — passwords here</option>
            </select>
            <span className="block text-xs text-gray-500 mt-1">SAML is not built yet.</span>
          </label>
          <Field label="Issuer address" value={form.sso_issuer}
                 onChange={e => setForm({ ...form, sso_issuer: e.target.value })}
                 placeholder="https://login.microsoftonline.com/<tenant-id>/v2.0"
                 help="Where their system publishes its settings. Microsoft 365: the address above with their tenant id. Google Workspace: https://accounts.google.com" />
          <Field label="Client ID" value={form.sso_client_id}
                 onChange={e => setForm({ ...form, sso_client_id: e.target.value })} />
          <Field label="Client secret" type="password" value={form.sso_client_secret}
                 onChange={e => setForm({ ...form, sso_client_secret: e.target.value })}
                 placeholder={org.sso_secret_set ? '•••••••• already stored' : ''}
                 help={org.sso_secret_set ? 'Leave blank to keep the stored one.' : undefined} />
        </div>

        <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 space-y-1">
          <p className="font-medium text-gray-700">Give their identity system this address:</p>
          {/* Sent by the server, never assembled here. The sign-in builds this address with the
              same function, so what is copied is exactly what will be sent. */}
          <CopyRow label="Redirect" value={org.redirect_uri || ''} />
          <p>
            It must match exactly, including https and any trailing slash. A mismatch is the
            single most common reason a first sign-in fails, and their system reports it rather
            than ours.
          </p>
          {/* Named because whoever fills this in is looking at somebody else's console and has
              to know which of its many values we are asking for. */}
          <p className="pt-1">
            <span className="font-medium text-gray-700">On Microsoft 365</span> these come from
            Entra ID → App registrations → New registration: the <em>Application (client) ID</em>,
            a secret from <em>Certificates &amp; secrets</em>, and the redirect above added as a
            <em> Web</em> platform. The tenant id is on the same overview page.
          </p>
        </div>

        <Button type="submit" busy={busy === 'save'}>Save sign-in details</Button>
      </form>

      <div className="pt-3 border-t border-gray-100 flex items-center gap-3 flex-wrap">
        {org.sso_enabled ? (
          <>
            <span className="text-sm text-green-700 inline-flex items-center gap-1.5">
              <Check className="w-4 h-4" /> On
              {org.sso_verified_at
                ? <span className="text-gray-500">· proved by a real sign-in</span>
                : <span className="text-amber-700">· not yet proved by a real sign-in</span>}
            </span>
            <Button kind="danger" busy={busy === 'toggle'} onClick={() => toggle(false)}>
              <Power className="w-3.5 h-3.5" /> Turn sign-in off
            </Button>
          </>
        ) : (
          <>
            <Button busy={busy === 'toggle'} disabled={!ready} onClick={() => toggle(true)}>
              <Power className="w-3.5 h-3.5" /> Turn sign-in on
            </Button>
            <span className="text-xs text-gray-500">
              {!domainProved
                ? 'Waiting on a verified domain.'
                : !ready
                  ? 'Fill in the issuer, client ID and secret first.'
                  : 'Passwords at their verified domains stop working the moment this goes on.'}
            </span>
          </>
        )}
      </div>
    </Panel>
  )
}

/* ── step 6: the boundary becomes real ───────────────────────────────────── */

function TheirDatabase({ org, after, setError }) {
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const provision = async () => {
    setBusy(true); setError(null)
    try {
      const { data } = await api.post(`/organisations/${org.id}/provision`)
      setConfirming(false)
      await after(data?.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not create their database.') }
    finally { setBusy(false) }
  }

  return (
    <Panel icon={Database} title="Their database"
           blurb="Their work lives somewhere our own queries do not point. This is what makes the separation real rather than a filter.">
      {org.database_ready ? (
        <p className="text-sm text-gray-700">
          <span className="text-green-700 inline-flex items-center gap-1 mr-1"><Check className="w-4 h-4" /></span>
          Ready — <code className="text-xs bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">{org.database_name}</code>
        </p>
      ) : confirming ? (
        <div className="space-y-2">
          <Problem>
            Creating a database is not something that undoes itself. Check the short name is
            right — <strong>{org.slug}</strong> — because the database is named from it and
            renaming it afterwards is a migration, not an edit.
          </Problem>
          <div className="flex gap-2">
            <Button busy={busy} onClick={provision}>Yes, create it</Button>
            <Button kind="plain" onClick={() => setConfirming(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <Button kind="plain" onClick={() => setConfirming(true)}>Create their database</Button>
          <span className="text-xs text-gray-500">
            Safe to run twice — an existing database is left exactly as it is.
          </span>
        </div>
      )}
    </Panel>
  )
}

/* ── their people ─────────────────────────────────────────────────────────── */

function People({ org, after, setError }) {
  const [people, setPeople] = useState(null)
  const [form, setForm] = useState({ email: '', role: '', is_org_admin: false })
  const [busy, setBusy] = useState(null)

  const load = async () => {
    try {
      const { data } = await api.get(`/organisations/${org.id}/members`)
      setPeople(data?.items || [])
    } catch { setPeople([]) }
  }
  useEffect(() => { load() }, [org.id])

  const add = async (e) => {
    e.preventDefault()
    setBusy('add'); setError(null)
    try {
      const body = { email: form.email.trim(), is_org_admin: form.is_org_admin }
      if (form.role.trim()) body.role = form.role.trim()
      const { data } = await api.post(`/organisations/${org.id}/members`, body)
      setForm({ email: '', role: '', is_org_admin: false })
      await load()
      await after(data?.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not add that person.') }
    finally { setBusy(null) }
  }

  const setAdmin = async (p, on) => {
    setBusy(p.user_id); setError(null)
    try {
      await api.put(`/organisations/${org.id}/members/${p.user_id}`, { is_org_admin: on })
      await load()
      await after(null)
    } catch (e) { setError(e.response?.data?.detail || 'Could not change that.') }
    finally { setBusy(null) }
  }

  const remove = async (p) => {
    setBusy(p.user_id); setError(null)
    try {
      const { data } = await api.delete(`/organisations/${org.id}/members/${p.user_id}`)
      await load()
      await after(data?.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not remove that person.') }
    finally { setBusy(null) }
  }

  return (
    <Panel icon={Users} title="Their people"
           blurb="Their own administrator normally does this. Here so the first one can be appointed — somebody has to be able to let the rest in.">
      {people === null ? (
        <p className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : people.length === 0 ? (
        <p className="text-sm text-gray-500">
          Nobody yet. Add their administrator first; they can then add everyone else.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {people.map(p => (
            <li key={p.user_id} className="px-3 py-2 flex items-center gap-3">
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-gray-900 truncate">{p.name}</span>
                <span className="block text-xs text-gray-500 truncate">{p.email} · {p.role}</span>
              </span>
              <label className="text-xs text-gray-600 inline-flex items-center gap-1.5 shrink-0">
                <input type="checkbox" checked={p.is_org_admin} disabled={busy === p.user_id}
                       onChange={e => setAdmin(p, e.target.checked)}
                       className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                their admin
              </label>
              <button onClick={() => remove(p)} disabled={busy === p.user_id}
                      className="text-gray-300 hover:text-red-600 disabled:opacity-40" title="Remove from this organisation">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={add} className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <Field label="Add somebody" type="email" required value={form.email}
               onChange={e => setForm({ ...form, email: e.target.value })}
               placeholder="person@acme.com"
               help="Must be at one of their verified domains. Works before they have ever signed in." />
        <label className="text-xs text-gray-600 inline-flex items-center gap-1.5 pb-2.5">
          <input type="checkbox" checked={form.is_org_admin}
                 onChange={e => setForm({ ...form, is_org_admin: e.target.checked })}
                 className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
          their admin
        </label>
        <div className="pb-0.5"><Button type="submit" busy={busy === 'add'}>Add</Button></div>
      </form>
    </Panel>
  )
}
